import { useState } from 'react';
import { BordButton } from 'bord-design-system';
import { EQuoteSlaType, IQuoteSummary, IQuoteDetailSla } from 'types/quote.types';
import { formatQuotePrice } from 'views/Quote/utils/formatQuotePrice';
import { QuoteService } from 'services/quote.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';

import './QuoteDetailSummaryStats.scss';

interface IQuoteDetailSummaryStats {
  summary?: IQuoteSummary;
  quoteId?: number;
  slas?: IQuoteDetailSla[];
}

/**
 * Estadísticas del resumen de cotización: herramientas, aprobadas, rechazadas, compradas y totales.
 */
const QuoteDetailSummaryStats = ({ summary, quoteId, slas }: IQuoteDetailSummaryStats) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const hasExpiration =
    slas?.some(sla => sla?.quoteSlaType?.code === EQuoteSlaType.CLIENT_RESPONSE_TIME && sla?.expiresAt) ?? false;

  const isDownloadDisabled = !hasExpiration || !quoteId;

  const handleDownload = async () => {
    if (!quoteId) return;
    setIsDownloading(true);
    try {
      const response = await QuoteService.generatePdf(quoteId);
      if (response.url) {
        window.open(response.url, '_blank');
      }
    } catch {
      displayErrorNotification();
    } finally {
      setIsDownloading(false);
    }
  };

  const saleCurrency = summary?.saleCurrency ?? 'USD';

  return (
    <>
      <div className="summaryCardsContainer">
        <div className="summaryCard">
          <div className="summaryRow">
            <span className="rowLabel">HERRAMIENTAS COTIZADAS</span>
            <span className="rowValueBold">{summary?.itemsQuantity ?? 0}</span>
          </div>
          <div className="summaryDivider" />
          <div className="summaryRow">
            <div className="rowLabelWithDot">
              <span className="dot green" />
              <span className="rowLabel">APROBADAS</span>
            </div>
            <span className="rowValue">{summary?.approvedQuantity ?? 0}</span>
          </div>
          <div className="summaryRow">
            <div className="rowLabelWithDot">
              <span className="dot red" />
              <span className="rowLabel">RECHAZADAS</span>
            </div>
            <span className="rowValue">{summary?.rejectedQuantity ?? 0}</span>
          </div>
        </div>
        <div className="summaryCardsDivider" />
        <div className="summaryCard">
          <div className="summaryRow">
            <div className="rowLabelWithDot">
              <span className="dot blue" />
              <span className="rowLabel">COMPRADAS</span>
            </div>
            <span className="rowValueBold">
              {summary?.purchasedQuantity ?? 0} de {summary?.approvedQuantity ?? 0}
            </span>
          </div>
          <div className="summaryDivider" />
          <div className="summaryRow">
            <span className="rowLabel">{`TOTAL APROBADO ${saleCurrency}`}</span>
            <span className="rowValueBold">{formatQuotePrice(summary?.totalApproved ?? 0, saleCurrency)}</span>
          </div>
          <div className="summaryRow">
            <span className="rowLabelTeal">{`TOTAL COMPRADO ${saleCurrency}`}</span>
            <span className="rowValueTeal">{formatQuotePrice(summary?.totalPurchased ?? 0, saleCurrency)}</span>
          </div>
        </div>
      </div>
      <BordButton
        label="Descargar cotización"
        bordButtonStyle="outlined"
        buttonIconOne="download"
        customClassName="downloadBtn"
        disabled={isDownloadDisabled || isDownloading}
        isLoading={isDownloading}
        onClick={handleDownload}
      />
    </>
  );
};

export default QuoteDetailSummaryStats;
